/**
 * 画像のみのaタグ処理機能を管理
 *
 * 仕様
 * - 画像のみを含むaタグ（classなし）を自動検出
 * - フォーカス時の表示改善のため'ist-js__imageLink'クラスを追加
 * - インライン画像のフォーカス枠を適切に表示するための処理
 *
 * 対象となるHTML例:
 * <a href="/image.jpg"><img src="thumb.jpg" alt="画像"></a>
 * <p><a href="/image.jpg"><img src="thumb.jpg" alt="画像"></a></p>
 *
 * 対象外:
 * <a href="/page.html"><img src="icon.jpg"> テキスト</a>
 * <a href="/image.jpg" class="existing"><img src="thumb.jpg"></a>
 *
 * CSS例:
 * a.ist-js__imageLink:focus {
 *   border-radius: 4px;
 *   box-shadow: 0 0 0 1px $colorHoverBackground, 0 0 0 2px $colorBlue01;
 *   outline: none !important;
 *   display: inline-block; // インライン画像の枠を適切に表示
 * }
 */

/**
 * 画像のみのaタグ処理機能を管理
 */
export class ImageLinkProcessor {
  private static readonly CLASS_NAME = 'ist-js__imageLink';
  private static readonly PROCESSED_ATTR = 'data-image-link-processed';

  private container: Document | Element;
  private processedLinks: HTMLAnchorElement[] = [];

  constructor(container: Document | Element = document) {
    this.container = container;
    this.init();
  }

  private init(): void {
    this.processImageLinks();
  }

  /**
   * 画像のみのaタグを処理してクラスを追加
   */
  private processImageLinks(): void {
    const links = this.container.querySelectorAll(
      'a'
    ) as NodeListOf<HTMLAnchorElement>;

    links.forEach((link) => {
      // 既に処理済みの場合はスキップ
      if (link.hasAttribute(ImageLinkProcessor.PROCESSED_ATTR)) {
        return;
      }

      // classが設定されていない、または空の場合のみ処理
      if (!link.className || link.className.trim() === '') {
        if (this.isImageOnlyContent(link)) {
          this.addImageLinkClass(link);
        }
      }

      // 処理済みマークを追加（重複処理を防ぐため）
      link.setAttribute(ImageLinkProcessor.PROCESSED_ATTR, 'true');
    });
  }

  /**
   * 要素の中身が画像のみかどうかをチェック
   */
  private isImageOnlyContent(element: HTMLElement): boolean {
    const childNodes = element.childNodes;
    let hasImage = false;

    for (let i = 0; i < childNodes.length; i++) {
      const node = childNodes[i];

      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;

        if (el.tagName.toLowerCase() === 'img') {
          hasImage = true;
        } else {
          // img以外の要素がある場合はfalse
          return false;
        }
      } else if (node.nodeType === Node.TEXT_NODE) {
        // テキストノードの場合、空白以外の文字があるかチェック
        const textContent = node.textContent || '';
        if (textContent.trim() !== '') {
          // 空白以外のテキストがある場合はfalse
          return false;
        }
      }
    }

    return hasImage;
  }

  /**
   * aタグに画像リンク用のクラスを追加
   */
  private addImageLinkClass(link: HTMLAnchorElement): void {
    link.classList.add(ImageLinkProcessor.CLASS_NAME);
    this.processedLinks.push(link);
  }

  /**
   * 新しく追加されたaタグを再処理
   */
  public refresh(): void {
    this.processImageLinks();
  }

  /**
   * 処理済みのリンク数を取得
   */
  public getProcessedLinksCount(): number {
    return this.processedLinks.length;
  }

  /**
   * 処理済みのリンクを取得
   */
  public getProcessedLinks(): HTMLAnchorElement[] {
    return [...this.processedLinks];
  }

  /**
   * 特定のリンクが処理済みかどうかをチェック
   */
  public isProcessed(link: HTMLAnchorElement): boolean {
    return link.hasAttribute(ImageLinkProcessor.PROCESSED_ATTR);
  }

  /**
   * 処理を取り消し（クラスを削除）
   */
  public removeImageLinkClass(link: HTMLAnchorElement): void {
    link.classList.remove(ImageLinkProcessor.CLASS_NAME);
    link.removeAttribute(ImageLinkProcessor.PROCESSED_ATTR);

    const index = this.processedLinks.indexOf(link);
    if (index > -1) {
      this.processedLinks.splice(index, 1);
    }
  }

  /**
   * 全ての処理を取り消し
   */
  public destroy(): void {
    this.processedLinks.forEach((link) => {
      link.classList.remove(ImageLinkProcessor.CLASS_NAME);
      link.removeAttribute(ImageLinkProcessor.PROCESSED_ATTR);
    });
    this.processedLinks = [];
  }
}

/**
 * 複数の画像リンク処理を管理するマネージャークラス
 */
export class ImageLinkManager {
  private processors: ImageLinkProcessor[] = [];
  private mutationObserver: MutationObserver | null = null;

  constructor() {
    this.init();
  }

  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeProcessors();
        this.setupMutationObserver();
      });
    } else {
      this.initializeProcessors();
      this.setupMutationObserver();
    }
  }

  /**
   * 初期処理を実行
   */
  private initializeProcessors(): void {
    const processor = new ImageLinkProcessor(document);
    this.processors.push(processor);
  }

  /**
   * 動的に追加されるコンテンツを監視
   */
  private setupMutationObserver(): void {
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldRefresh = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              // aタグが追加された場合、または aタグを含む要素が追加された場合
              if (element.tagName === 'A' || element.querySelector('a')) {
                shouldRefresh = true;
              }
            }
          });
        }
      });

      if (shouldRefresh) {
        this.refresh();
      }
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * 全ての処理を再実行
   */
  public refresh(): void {
    this.processors.forEach((processor) => processor.refresh());
  }

  /**
   * 処理済みリンクの総数を取得
   */
  public getTotalProcessedLinksCount(): number {
    return this.processors.reduce(
      (total, processor) => total + processor.getProcessedLinksCount(),
      0
    );
  }

  /**
   * 全ての処理済みリンクを取得
   */
  public getAllProcessedLinks(): HTMLAnchorElement[] {
    return this.processors.reduce(
      (allLinks, processor) => [...allLinks, ...processor.getProcessedLinks()],
      [] as HTMLAnchorElement[]
    );
  }

  /**
   * 特定の要素内の画像リンクを処理
   */
  public processContainer(container: Element): ImageLinkProcessor {
    const processor = new ImageLinkProcessor(container);
    this.processors.push(processor);
    return processor;
  }

  /**
   * 全ての処理を破棄
   */
  public destroy(): void {
    this.processors.forEach((processor) => processor.destroy());
    this.processors = [];

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }
}
